import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AcknowledgeModal, ActionModal, Flex, Modal, Text } from '@percent/lemonade'
import { useMutation } from '@percent/partner-dashboard/common/hooks'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'

export type RemoveOrganisationFromCategoryModalProps = Readonly<{
  categoryId: string
  organisation: { id: string; name: string }
  open: boolean
  onClose: () => void
  onSuccess: (removedOrganisationId: string) => void
}>

export function RemoveOrganisationFromCategoryModal({
  categoryId,
  organisation,
  open,
  onClose,
  onSuccess
}: RemoveOrganisationFromCategoryModalProps) {
  const { t } = useTranslation()
  const { categoryService } = useServices()
  const [hasFailed, setHasFailed] = useState(false)

  const [{ isLoading }, { apiFunc: removeOrganisationFromCategory }] = useMutation(
    categoryService.removeOrganisationFromCategory,
    () => {
      onSuccess(organisation.id)
      onClose()
    },
    () => setHasFailed(true)
  )

  const handleClose = () => {
    setHasFailed(false)
    onClose()
  }

  const handleSubmit = () => {
    removeOrganisationFromCategory({
      categoryId,
      organisationId: organisation.id
    })
  }

  return (
    <Modal open={open} onClose={handleClose} aria-labelledby="remove-organisation-from-category-modal">
      {hasFailed ? (
        <AcknowledgeModal
          result="negative"
          title={t('categories.removeOrganisation.modal.error.title')}
          description={t('categories.removeOrganisation.modal.error.description', {
            organization: organisation.name
          })}
          buttonText={t('button.close')}
          handleClose={handleClose}
          viewTestId="remove-organisation-from-category-error"
        />
      ) : (
        <ActionModal
          title={t('categories.removeOrganisation.modal.title')}
          primaryButtonText={t('button.remove')}
          secondaryButtonText={t('button.cancel')}
          handleClose={handleClose}
          handleSubmit={handleSubmit}
          loading={isLoading}
          primaryBtnTestId="remove-organisation-from-category-submit"
        >
          <Flex direction="column" gap="sm">
            <Text as="p">
              {t('categories.removeOrganisation.modal.description', { organization: organisation.name })}
            </Text>
            <Text as="p">{t('categories.removeOrganisation.modal.confirmation')}</Text>
          </Flex>
        </ActionModal>
      )}
    </Modal>
  )
}
